import { Intent } from "@blueprintjs/core";
import { addMinutes, format, parseJSON } from "date-fns";
import {
  ButtonContainer,
  DataTable,
  FGListen,
  FGNumberInput,
  FGTextAreaInput,
  FieldGroup,
  IDataTableColumn,
  showError,
  useGridState
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import { ExamenEntreeFceDocumentEditDialog, ExamenEntreeFceDocumentUploadDialog } from ".";
import {
  AcquisAdmissionApi,
  ExamenEntreeFceEditDtoFromJSON,
  ExamenEntreeFceFichierGridDto
} from "../../../../../../api";
import {
  AddButton,
  DeleteButton,
  DownloadButton,
  EditButton,
  FGWalterDateMaskInput,
  FGWalterSelectInput,
  SmallFormGenerator
} from "../../../../../../components";
import { useDialog } from "../../../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";
import { exportFile } from "../../../../../../utils";

export interface IExamenEntreeFceDetailProps {
  onTypeAcquisChange?: (typeAcquis: string) => void;
  idpersonne: number;
  listRoute: string;
}

export const ExamenEntreeFceDetail: React.FunctionComponent<IExamenEntreeFceDetailProps> = ({
  onTypeAcquisChange,
  idpersonne,
  listRoute
}) => {
  const { t } = useTl();
  const api = useApiService(AcquisAdmissionApi);
  const [typeAcquis, taLoading] = useReferential(a => a.referentialGetAcquisAdmissionTypes(), false, []);
  const [anneeFormation, afLoading] = useReferential(a => a.referentialGetAnneeScolaire(), false, []);
  const [acquisCom, acLoading] = useReferential(a => a.referentialGetAcquisCommentaires(), true, []);
  const [resultats, resuLoading] = useReferential(a => a.referentialGetResultatExamenEntreeFce(), true, []);
  const [decisionDossierAdmission, ddaLoading] = useReferential(
    a => a.referentialGetDecisionDossierAdmissions(),
    true,
    []
  );
  const [examinateurs, eLoading] = useReferential(a => a.referentialGetCentreGlobalUsers({ currentIduser: 0 }));
  const history = useHistory();
  const { state, acquisId } = useParams<{
    id: string;
    tab: string;
    state: string;
    acquisId: string;
  }>();
  const editMode = React.useMemo(() => state === "edit", [state]);

  const { data, loading, saveItem, saving, deleteItem, deleting, validationErrors, refresh: refreshData } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: async () => {
          return +acquisId <= 0
            ? ExamenEntreeFceEditDtoFromJSON({
                idexamenEntreeFce: +acquisId,
                idpersonne: idpersonne,
                typeAcquis: "ExamenEntreeFCE"
              })
            : api.acquisAdmissionGetExamenEntreeFce({ id: +acquisId });
        },
        saveApiFn: d => api.acquisAdmissionSaveExamenEntreeFce({ ExamenEntreeFceEditDto: d }),
        onSavedRoute: d => listRoute,
        getDeps: [acquisId],
        deleteApiFn: d => api.acquisAdmissionDeleteExamenEntreeFce({ id: d.idexamenEntreeFce }),
        onDeletedRoute: () => listRoute
      }),
      [acquisId, idpersonne, api, listRoute]
    )
  );
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      typeAcquis: Yup.string().required(t(ETLCodes.Required)),
      date: Yup.date().typeError(t(ETLCodes.DateInvalide))
    });
  }, [t]);

  const tableState = useGridState<any>({
    serverMode: false,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { uploadDate: "DESC", fileName: "ASC" }
  });

  const { setData } = tableState;

  React.useEffect(() => {
    if (data?.fichiers) {
      setData(data.fichiers);
    }
  }, [data?.fichiers, setData]);

  const [currentExamenEntreeFceFichierId, setCurrentExamenEntreeFceFichierId] = React.useState(null);
  const [uploadDialoOpen, setUploadDialoOpen] = React.useState(false);
  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idexamenEntreeFceFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idexamenEntreeFceFichier);
          await api.acquisAdmissionDeleteExamenEntreeFceFichier({ id: idexamenEntreeFceFichier });
          refreshData();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, refreshData, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idexamenEntreeFceFichier: number) => {
      try {
        setRowLoading(idexamenEntreeFceFichier);
        const file = await api.acquisAdmissionDownloadExamenEntreeFceFichier({ id: idexamenEntreeFceFichier });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: ExamenEntreeFceFichierGridDto) => (
          <ButtonContainer>
            <EditButton
              minimal={true}
              onClick={() => setCurrentExamenEntreeFceFichierId(row.idexamenEntreeFceFichier)}
              loading={rowLoading === row.idexamenEntreeFceFichier}
              disabled={!!rowLoading && rowLoading !== row.idexamenEntreeFceFichier}
            />
            <DownloadButton
              minimal={true}
              onClick={() => downloadFichier(row.idexamenEntreeFceFichier)}
              loading={rowLoading === row.idexamenEntreeFceFichier}
              disabled={!!rowLoading && rowLoading !== row.idexamenEntreeFceFichier}
            />
            <DeleteButton
              minimal={true}
              onDelete={() => deleteFichier(row.idexamenEntreeFceFichier)}
              loading={rowLoading === row.idexamenEntreeFceFichier}
              disabled={!!rowLoading && rowLoading !== row.idexamenEntreeFceFichier}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.NomFichier),
        fieldName: "fileName"
      },
      {
        header: () => t(ETLCodes.DateUpload),
        fieldName: "uploadDate"
      },
      {
        header: () => t(ETLCodes.UtilisateurUpload),
        fieldName: "creationUserName"
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "typeAcquisFichier"
      }
    ],
    [deleteFichier, downloadFichier, rowLoading, t]
  );

  const formatDate = React.useCallback((date: Date) => {
    return format(parseJSON(addMinutes(date, date.getTimezoneOffset())), "dd/MM/yyyy HH:mm");
  }, []);

  const onClose = React.useCallback(
    (refresh?: boolean) => {
      setCurrentExamenEntreeFceFichierId(null);
      setUploadDialoOpen(false);
      if (refresh) {
        refreshData();
      }
    },
    [refreshData]
  );

  const onAdd = React.useCallback(() => {
    setUploadDialoOpen(true);
  }, []);

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      showColons
      editMode={editMode}
      minLabelWidth={190}
      labelAlignment="right"
      formatDate="dd/MM/yyyy"
      validationSchema={FormSchema}
      loading={loading}
      onCancel={() => history.push(listRoute)}
      saving={saving}
      showDeleteButton={+acquisId > 0}
      onDelete={deleteItem}
      deleting={deleting}
      validationErrors={validationErrors}
    >
      <FieldGroup columns={2}>
        <FieldGroup
          fieldsetProps={{
            title: t(ETLCodes.ExamenEntreeFCE)
          }}
        >
          <FGListen field="typeAcquis" onChanged={onTypeAcquisChange}></FGListen>
          <FGWalterSelectInput
            label={t(ETLCodes.Type)}
            name="typeAcquis"
            items={typeAcquis}
            loading={taLoading}
            readonly={+(acquisId ?? 0) > 0}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.Annee)}
            name="anneeDeFormation"
            items={anneeFormation}
            loading={afLoading}
          />
          <FGWalterDateMaskInput label={t(ETLCodes.Date)} name="date" />
          <FGNumberInput label={t(ETLCodes.PointsMathematiques)} name="pointsMath" />
          <FGNumberInput label={t(ETLCodes.PointsFrancais)} name="pointsFrancais" />
          <FGNumberInput label={t(ETLCodes.PointsLogique)} name="pointsLogique" />
          <FGWalterSelectInput
            label={t(ETLCodes.DecisionDossierAdmission)}
            name="decisionDossierAdmission"
            items={decisionDossierAdmission}
            loading={ddaLoading}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.Examinateur)}
            name="examinateur"
            items={examinateurs}
            loading={eLoading}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.ResultatFinal)}
            name="resultatFinal"
            items={resultats}
            loading={resuLoading}
          />
          <FGWalterSelectInput
            label={t(ETLCodes.CommentaireDeliberation)}
            name="commentaire"
            items={acquisCom}
            loading={acLoading}
          />
          <FGTextAreaInput label={t(ETLCodes.Remarque)} name="remarque" />
        </FieldGroup>
      </FieldGroup>
      <FieldGroup
        visible={+acquisId > 0}
        fieldsetProps={{
          title: t(ETLCodes.Documents),
          rightElement: (
            <AddButton
              onClick={e => {
                e.stopPropagation();
                onAdd();
              }}
              text={t(ETLCodes.General_Add)}
              intent={Intent.PRIMARY}
            />
          )
        }}
      >
        <DataTable
          dateFormat="dd/MM/yyyy HH:mm"
          formatDate={formatDate}
          tableState={tableState}
          loading={loading}
          columns={columns}
        ></DataTable>
        {uploadDialoOpen && (
          <ExamenEntreeFceDocumentUploadDialog
            onClose={onClose}
            dialogOpen={uploadDialoOpen}
            idexamenEntreeFce={+acquisId}
          />
        )}
        {!!currentExamenEntreeFceFichierId && (
          <ExamenEntreeFceDocumentEditDialog
            onClose={onClose}
            idexamenEntreeFceFichier={currentExamenEntreeFceFichierId}
          />
        )}
      </FieldGroup>
    </SmallFormGenerator>
  );
};
